import * as THREE from 'three';
import { BoxCollider } from './Collider.js';

const _hit = {};
const _contact = {};
const _tmp = new THREE.Vector3();

/**
 * Collision world. Flat array plus AABB rejection: the levels are hand built
 * from a few hundred boxes, which is well inside the range where a broadphase
 * would cost more than it saves.
 */
export class PhysicsWorld {
  constructor() {
    /** @type {BoxCollider[]} */
    this.colliders = [];
    this.gravity = new THREE.Vector3(0, -26, 0);
    /** Colliders temporarily ignored, keyed by id. Portals punch holes here. */
    this.ignored = new Set();
  }

  add(collider) { this.colliders.push(collider); return collider; }

  addBox(def) { return this.add(new BoxCollider(def)); }

  remove(collider) {
    const i = this.colliders.indexOf(collider);
    if (i >= 0) this.colliders.splice(i, 1);
  }

  clear() { this.colliders.length = 0; this.ignored.clear(); }

  /**
   * Closest hit along a ray.
   * @param {(c: BoxCollider) => boolean} [filter]
   */
  raycast(origin, dir, maxDist = 200, filter = null, opts = {}) {
    let best = null;
    let bestDist = maxDist;
    const includeDisabled = opts.includeDisabled === true;
    for (const c of this.colliders) {
      // Portal hosts stay ray-visible while disabled: the gun must still be
      // able to target the wall it already punched a hole in.
      if ((!c.enabled && !includeDisabled) || c.trigger) continue;
      if (filter && !filter(c)) continue;
      const h = c.raycast(origin, dir, bestDist, _hit);
      if (h && h.distance < bestDist) {
        bestDist = h.distance;
        best = best || { point: new THREE.Vector3(), normal: new THREE.Vector3() };
        best.point.copy(h.point);
        best.normal.copy(h.normal);
        best.distance = h.distance;
        best.axis = h.axis;
        best.sign = h.sign;
        best.collider = c;
      }
    }
    return best;
  }

  /**
   * Push a capsule out of everything it overlaps.
   * Mutates the capsule in place and reports what it touched.
   */
  resolveCapsule(capsule, result, opts = {}) {
    const iterations = opts.iterations ?? 4;
    const skip = opts.skip || null;

    result.grounded = false;
    result.ceiling = false;
    result.wall = false;
    result.groundNormal.set(0, 1, 0);
    result.groundCollider = null;
    result.displacement.set(0, 0, 0);
    result.contactCount = 0;

    let bestGroundY = -1;

    for (let it = 0; it < iterations; it++) {
      let moved = false;
      for (const c of this.colliders) {
        if (!c.enabled || c.trigger) continue;
        if (this.ignored.has(c.id)) continue;
        if (skip && skip(c, capsule)) continue;

        const contact = c.capsuleContact(capsule, _contact);
        if (!contact) continue;

        // Destructible surfaces decide at contact, before resolution eats
        // the incoming velocity. Frame-level trigger shells alone can be
        // crossed between updates by a fast body.
        c.onContact?.(contact, opts.velocity);
        if (!c.enabled) continue;

        // A hair of extra push stops the capsule from re-touching next frame,
        // which would otherwise flicker the grounded flag on flat floors.
        const push = contact.depth + 1e-4;
        _tmp.copy(contact.normal).multiplyScalar(push);
        capsule.translate(_tmp);
        result.displacement.add(_tmp);
        result.contactCount++;
        moved = true;

        const ny = contact.normal.y;
        if (ny > 0.55) {
          result.grounded = true;
          if (ny > bestGroundY) {
            bestGroundY = ny;
            result.groundNormal.copy(contact.normal);
            result.groundCollider = c;
          }
        } else if (ny < -0.55) {
          result.ceiling = true;
        } else {
          result.wall = true;
          result.wallNormal.copy(contact.normal);
        }
      }
      if (!moved) break;
    }
    return result;
  }

  /** Colliders whose AABB contains the point, for trigger volumes. */
  queryPoint(p, out = []) {
    out.length = 0;
    for (const c of this.colliders) {
      if (!c.enabled) continue;
      if (p.x < c.aabb.min.x || p.x > c.aabb.max.x) continue;
      if (p.y < c.aabb.min.y || p.y > c.aabb.max.y) continue;
      if (p.z < c.aabb.min.z || p.z > c.aabb.max.z) continue;
      if (c.containsPoint(p)) out.push(c);
    }
    return out;
  }
}

/** Reusable output record for resolveCapsule. */
export function makeCollisionResult() {
  return {
    grounded: false,
    ceiling: false,
    wall: false,
    groundNormal: new THREE.Vector3(0, 1, 0),
    wallNormal: new THREE.Vector3(),
    groundCollider: null,
    displacement: new THREE.Vector3(),
    contactCount: 0,
  };
}
